class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isDrawer = this.getAttribute('data-search-drawer') === 'true';
    this.isSearchPage = this.getAttribute('data-search-page') === 'true';
    this.searchDrawer = this.isDrawer ? this.closest('search-drawer') : null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener('focus', this.onFocus.bind(this));
    if (!this.isDrawer) {
      this.addEventListener('focusout', this.onFocusOut.bind(this));
    }
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onResultsClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.notifyDrawer();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.notifyDrawer();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onResultsClick(event) {
    const tab = event.target.closest('[data-search-tab]');
    if (!tab) return;
    event.preventDefault();
    this.activateTab(tab.getAttribute('data-search-tab'));
  }

  activateTab(tabName) {
    if (!tabName) return;
    this.querySelectorAll('[data-search-tab]').forEach((btn) => {
      const selected = btn.getAttribute('data-search-tab') === tabName;
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
    this.querySelectorAll('[data-search-panel]').forEach((panel) => {
      const active = panel.getAttribute('data-search-panel') === tabName;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('li[role="option"], button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[type]': 'product,collection,article,page,query',
      'resources[options][fields]': 'title,body,product_type,variants.title,vendor,tag',
      section_id: 'predictive-search',
    });

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.revealSearchPageColumns();

    this.setLiveRegionResults();
    this.open();
    this.notifyDrawer();
  }

  /**
   * Search results page: show every category at once (flex columns / stacked
   * mobile). The shared markup still uses tab `hidden` for the header drawer.
   */
  revealSearchPageColumns() {
    if (this.isDrawer || !this.isSearchPage) return;

    this.querySelectorAll('[data-search-panel]').forEach((panel) => {
      panel.removeAttribute('hidden');
      panel.classList.add('is-active');
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getResultsMaxHeight() {
    if (this.isDrawer) return null;
    if (this.isSearchPage) {
      const shell = this.querySelector('.search-page__bar') || this.predictiveSearchResults;
      const bottom = shell?.getBoundingClientRect?.().bottom || 0;
      this.resultsMaxHeight = Math.max(window.innerHeight - bottom - 16, 240);
      return this.resultsMaxHeight;
    }
    const header = document.getElementById('shopify-section-header');
    if (!header) return window.innerHeight;
    this.resultsMaxHeight = window.innerHeight - header.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.isDrawer) {
      const maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
      if (maxHeight) {
        this.predictiveSearchResults.style.maxHeight = `${maxHeight}px`;
      }
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.notifyDrawer();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      // Drop in-flight fetches so a late response can't repopulate results after close/clear
      try {
        this.abortController.abort();
      } catch (error) {
        console.error('PredictiveSearch: failed to abort in-flight request', error);
      }
      this.abortController = new AbortController();

      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = `
          <div class="predictive-search__loading-state" aria-hidden="true">
            <div class="loading-overlay">
              <div class="loading-overlay__spinner">
                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
            </div>
          </div>
        `;
      }
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  notifyDrawer() {
    if (!this.searchDrawer) return;
    const hasQuery = Boolean(this.getQuery());
    const resultsRoot = this.querySelector('#predictive-search-results');
    const hasResultsAttr = resultsRoot?.getAttribute('data-has-results');
    const hasResults =
      this.getAttribute('results') === 'true' &&
      (hasResultsAttr === null || hasResultsAttr === 'true' || hasQuery);

    document.dispatchEvent(
      new CustomEvent('search-drawer:results', {
        detail: {
          source: this.searchDrawer,
          hasQuery,
          hasResults: hasQuery,
        },
      })
    );
  }
}

customElements.define('predictive-search', PredictiveSearch);
